import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowUpRight, ArrowRight, Quote, CheckCircle2 } from "lucide-react";
import Marquee from "react-fast-marquee";
import { useSeo } from "@/lib/seo";
import { Reveal } from "@/components/Reveal";
import { LogoMark } from "@/components/Logo";
import { LeadForm } from "@/components/LeadForm";
import { ComplianceNote } from "@/components/ComplianceNote";
import { businessLines } from "@/data/businessLines";
import { kpis, principles, founders, payoff, payoffSub } from "@/data/content";

const HERO_IMG =
  "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBtb2Rlcm4lMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzg3ODE5NzkxfDA&ixlib=rb-4.1.0&q=85&w=1600";

export default function Home() {
  useSeo({
    title: "Investimento immobiliare etico ad alta redditività",
    description:
      "Opera Real Estate Partners: boutique di investimento immobiliare. Aste, saldo e stralcio, sviluppo, affitti brevi, student housing, club deal. Il Metodo prima del Mattone.",
  });

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden noise-overlay">
        <div className="absolute inset-0 z-0">
          <img src={HERO_IMG} alt="Architettura contemporanea" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0D1B2A]/85" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0D1B2A] via-[#0D1B2A]/70 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8 py-28 w-full">
          <Reveal>
            <div className="flex items-center gap-3 mb-8">
              <LogoMark className="h-12 w-12" />
              <div className="h-8 w-px bg-[#C5A059]/40" />
              <span className="label-eyebrow">Advisory Patrimoniale &amp; Valorizzazione Immobiliare</span>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-4xl text-white">
              Trasformiamo gli asset immobiliari complessi in{" "}
              <span className="text-[#C5A059]">rendimento prevedibile</span>, senza mai speculare
              sulla fragilità di chi vende.
            </h1>
          </Reveal>

          <Reveal delay={0.2}>
            <p className="font-serif-claim italic text-2xl sm:text-3xl text-gray-200 mt-8">
              {payoff}. <span className="text-[#C5A059]">{payoffSub}</span>
            </p>
          </Reveal>

          <Reveal delay={0.26}>
            <p className="text-gray-300 text-base sm:text-lg mt-5 max-w-2xl leading-relaxed">
              Analisi quantitativa, due diligence rigorosa e sviluppo di progetti residenziali ad
              alto rendimento a Pisa e in Toscana. Il metodo analitico prima del mattone.
            </p>
          </Reveal>

          <Reveal delay={0.3}>
            <div className="flex flex-col sm:flex-row gap-4 mt-12">
              <Link
                to="/contatti"
                data-testid="hero-cta-primary"
                className="group bg-[#C5A059] text-[#0D1B2A] hover:bg-[#D8B978] transition-colors font-medium px-8 py-4 flex items-center justify-center gap-2"
              >
                Richiedi un'Analisi Strategica
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/casi-studio"
                data-testid="hero-cta-secondary"
                className="group bg-transparent border border-white/30 text-white hover:border-[#C5A059] hover:text-[#C5A059] transition-colors font-medium px-8 py-4 flex items-center justify-center gap-2"
              >
                Scopri il Metodo OPERA
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </Reveal>

          <Reveal delay={0.38}>
            <div
              className="flex flex-wrap items-center gap-x-3 gap-y-2 mt-8 text-sm text-gray-400"
              data-testid="hero-trust-badges"
            >
              <span>Due Diligence Integrata</span>
              <span className="text-[#C5A059]">•</span>
              <span>Operazioni Off-Market</span>
              <span className="text-[#C5A059]">•</span>
              <span>Governance Trasparente</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* BUSINESS LINES GRID */}
      <section className="bg-[#0D1B2A] py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal>
            <span className="label-eyebrow">Cosa facciamo</span>
            <h2 className="font-display font-light text-3xl lg:text-4xl text-white mt-4 max-w-2xl">
              Otto linee di business, un solo metodo
            </h2>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 mt-14 border-t border-l grid-line">
            {businessLines.map((b, i) => {
              const Icon = Icons[b.icon] || Icons.Building2;
              return (
                <Reveal key={b.slug} delay={(i % 4) * 0.06}>
                  <Link
                    to={`/linee/${b.slug}`}
                    data-testid={`home-line-${b.slug}`}
                    className="group block h-full p-8 border-r border-b grid-line hover:bg-white/[0.03] transition-colors relative"
                  >
                    <Icon className="text-[#C5A059]" size={30} strokeWidth={1.5} />
                    <h3 className="font-display text-lg text-white mt-6 flex items-center gap-1">
                      {b.name}
                    </h3>
                    <p className="text-sm text-gray-400 mt-3 leading-relaxed">{b.oneLiner}</p>
                    <ArrowUpRight
                      className="text-[#C5A059] opacity-0 group-hover:opacity-100 transition-opacity absolute top-8 right-8"
                      size={18}
                    />
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* KPI DASHBOARD */}
      <section className="bg-[#F8F9FA] py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal>
            <span className="label-eyebrow">Numeri e metodo</span>
            <h2 className="font-display font-light text-3xl lg:text-4xl text-[#0D1B2A] mt-4 max-w-2xl">
              Target di rendimento, comunicati con onestà
            </h2>
            <p className="text-gray-600 mt-4 max-w-2xl leading-relaxed">
              I valori seguenti sono <strong className="text-[#0D1B2A]">obiettivi di operazione</strong>,
              non garanzie. Li rendiamo espliciti perché la trasparenza sui numeri è parte del metodo.
            </p>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-px mt-14 bg-[#0D1B2A]/10">
            {kpis.map((k, i) => (
              <Reveal key={k.label} delay={i * 0.1}>
                <div className="bg-white p-10 h-full">
                  <div className="label-eyebrow">{k.label}</div>
                  <div className="font-display font-light text-6xl text-[#C5A059] mt-4 tabular-nums">
                    {k.value}
                  </div>
                  <p className="text-sm text-gray-500 mt-4">{k.note}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-6">
            * Target riferiti alla singola operazione. I risultati passati non sono indicativi di
            rendimenti futuri.
          </p>
        </div>
      </section>

      {/* MANIFESTO MARQUEE */}
      <section className="bg-[#0D1B2A] py-10 border-y border-[#C5A059]/20">
        <Marquee speed={38} gradient={false} pauseOnHover>
          {["Metodo", "Etica", "Rendimento", "Trasparenza", "Tutela", "Disciplina"].map((w, i) => (
            <span key={i} className="mx-8 flex items-center gap-8">
              <span className="font-serif-claim italic text-3xl text-[#C5A059]">{w}</span>
              <span className="text-white/20 text-2xl">•</span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* ETHICAL PRINCIPLES */}
      <section className="bg-[#0D1B2A] py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal>
            <span className="label-eyebrow">Carta dei Principi Etici</span>
            <h2 className="font-display font-light text-3xl lg:text-5xl text-white mt-4 max-w-3xl leading-tight">
              Quattro principi cardine che vengono prima del profitto
            </h2>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-px mt-16 bg-white/5">
            {principles.map((p, i) => (
              <Reveal key={p.n} delay={(i % 2) * 0.1}>
                <div className="bg-[#0D1B2A] p-10 h-full group hover:bg-white/[0.02] transition-colors">
                  <div className="font-serif-claim text-5xl text-[#C5A059]/40 group-hover:text-[#C5A059] transition-colors">
                    {p.n}
                  </div>
                  <h3 className="font-display text-xl text-white mt-4">{p.title}</h3>
                  <p className="text-gray-400 mt-3 leading-relaxed">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FOUNDERS */}
      <section className="bg-[#F8F9FA] py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            <Reveal>
              <span className="label-eyebrow">Chi siamo</span>
              <h2 className="font-display font-light text-3xl lg:text-4xl text-[#0D1B2A] mt-4">
                Visione Finanziaria e Competenze Operative
              </h2>
              <p className="text-gray-600 mt-5 leading-relaxed">
                Un approccio disciplinato e quantitativo: strutturazione strategica del capitale,
                rigore metodologico e gestione operativa sul campo. Nessun compromesso sull'etica.
              </p>
              <Link
                to="/chi-siamo"
                data-testid="home-about-cta"
                className="inline-flex items-center gap-2 text-[#C5A059] mt-8 hover:gap-3 transition-all"
              >
                Scopri la nostra storia <ArrowRight size={16} />
              </Link>
            </Reveal>

            {founders.map((f, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="bg-[#0D1B2A] border border-white/5 h-full">
                  <div className="aspect-[4/3] overflow-hidden bg-[#091623] flex items-center justify-center relative noise-overlay">
                    <div className="relative z-10 h-24 w-24 rounded-full border-2 border-[#C5A059]/60 flex items-center justify-center bg-[#0D1B2A]">
                      <span className="font-display font-extrabold text-3xl text-[#C5A059] tracking-wider">{f.initials}</span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="label-eyebrow">{f.role}</div>
                    <h3 className="font-display text-lg text-white mt-2">{f.name}</h3>
                    <p className="text-sm text-gray-400 mt-2">{f.area}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT FORM — vendere o comprare (ben visibile) */}
      <section id="contatto" className="bg-[#1E2229] py-24 border-t border-white/5 scroll-mt-24">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 grid lg:grid-cols-5 gap-14">
          <div className="lg:col-span-2">
            <span className="label-eyebrow">Vendere o comprare</span>
            <h2 className="font-display font-light text-3xl lg:text-4xl text-white mt-4 leading-tight">
              Hai un immobile da vendere o vuoi acquistare?
            </h2>
            <p className="text-gray-400 mt-5 leading-relaxed">
              Scrivici in modo semplice e riservato. Un socio ti risponde con una valutazione
              concreta e senza impegno — che tu voglia vendere, anche in situazioni complesse, o
              acquistare a valore.
            </p>
            <ul className="mt-8 space-y-4">
              {[
                "Risposta rapida e riservata",
                "Nessuna pressione, nessun obbligo",
                "Valutazione onesta, basata sui numeri",
              ].map((t) => (
                <li key={t} className="flex items-center gap-3 text-gray-300">
                  <CheckCircle2 className="text-[#C5A059] shrink-0" size={18} strokeWidth={1.5} />
                  {t}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-3">
            <div className="bg-[#0D1B2A] border border-[#C5A059]/25 p-6 lg:p-8">
              <LeadForm
                showProfile
                sourcePage="Home — vendere o comprare"
                submitLabel="Invia la richiesta"
              />
            </div>
            <ComplianceNote className="mt-6" />
          </div>
        </div>
      </section>

      {/* CLOSING QUOTE */}
      <section className="bg-[#0D1B2A] py-24 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center">
          <Quote className="mx-auto text-[#C5A059]" size={40} strokeWidth={1.5} />
          <p className="font-serif-claim italic text-3xl lg:text-4xl text-white mt-8 leading-snug">
            Il valore vero nasce dal metodo, non dall'urgenza. Costruiamo rendimento dove altri
            vedono solo complessità.
          </p>
          <a
            href="#contatto"
            data-testid="home-contact-cta"
            className="inline-block mt-10 bg-[#C5A059] text-[#0D1B2A] hover:bg-[#D8B978] transition-colors font-medium px-8 py-4"
          >
            Parliamone
          </a>
        </div>
      </section>
    </div>
  );
}
